import * as React from "react";
import type { JSX } from "react";

import { AsyncComponent } from "@impero-com/common/components/asyncComponent";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as DV from "@impero-com/common/utils/dataView";
import {
    escapeMarkdownValue,
    formatMoment,
    formatUserName,
} from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Alert,
    Divider,
    emitNotification,
    PageSpinner,
    renderMarkdown,
    SelectedTabParam,
    Tabs,
    TabSpec,
} from "@impero-com/ui";
import * as Sentry from "@sentry/react";
import moment, { Moment } from "moment-timezone";
import PQueue from "p-queue";

import type { UserProfile } from "api/generated/model";
import {
    SetRiskAssessment,
    UpdatedMitigatingControl,
    MetadataInstanceValue,
    ActivityLogForRiskNodeActivityDto as RiskActivityLogDto,
    ActivityLogForRiskNodeActivity,
    RiskActivityLogLinkedResources,
    RiskNodeActivity,
    MetadataValue,
    HistoricControl,
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
    UserViewDto,
    IanaTimezoneView,
    ControlOverviewListDto,
    EntityView,
    SavedBlob,
} from "api/generated/model";
import { getRisksRiskDirectoryIdNodesNodeIdActivityLog } from "api/generated/risks/risks";
import { getUsersUserId } from "api/generated/users/users";
import { ActivityLog } from "components/activityLog";
import { ConditionalDisplay } from "components/conditionalDisplay";
import { Drawer } from "components/drawer";
import { formatBlobUrl } from "components/fileUploader/utils";
import { DateChange } from "org/risks/content/map";
import * as R from "org/risks/types";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";
import { getValueAt, getValueOfResourceAt } from "utils/activityLog";
import { Guid } from "utils/guid";

import { EnumMap, getEnum } from "../impactLikelihood";
import { CustomizedValidations, PartialSharedProperties } from "../sections";
import * as T from "../types";
import {
    filterAndResolveDirectorySections,
    getFieldIdsWithoutValueForAssessment,
    hasFormula,
} from "../utils";

import AssessmentCheckbox from "./assessmentCheckbox";
import MapDrawerForm from "./form";
import * as theme from "./theme.css";
import UserWithTimestampStatus from "./userWithTimestampStatus";

const ACTIVITY_TAB = "activity-tab";
const DETAILS_TAB = "details-tab";
type CurrentTab = typeof ACTIVITY_TAB | typeof DETAILS_TAB;

class MapNodeDrawer extends React.PureComponent<
    MapNodeDrawerProps,
    MapNodeDrawerState
> {
    asyncComponent = React.createRef<AsyncComponent<RiskActivityLogDto>>();
    private queue: PQueue = new PQueue({ concurrency: 1 });

    constructor(props: MapNodeDrawerProps) {
        super(props);
        this.state = {
            currentTab: DETAILS_TAB,
            customizedValidations: {},
        };
    }

    componentWillUnmount(): void {
        this.queue.clear();
    }

    renderAlert = () => {
        const { historyMode, historyDate } = this.props;

        if (!historyMode) {
            return null;
        }

        const date = formatMoment(historyDate ?? moment());

        return (
            <div className={theme.historyDrawerAlertContainer}>
                <Alert
                    message={t(
                        "RiskMap_History_Drawer_HistoricDataAlert_Message"
                    )}
                    description={t(
                        "RiskMap_History_Drawer_HistoricDataAlert_Description",
                        { date }
                    )}
                    type="info"
                />
            </div>
        );
    };

    changeAsssesmentStatus = (assessmentStatus: SetRiskAssessment) => {
        if (assessmentStatus === "assessed") {
            const { riskDirectoryView, metadataExtractors, node } = this.props;
            const requiresControlOnReduction =
                riskDirectoryView.content.resource
                    .requireControlOnRiskReduction;
            const response = this.getResponse();
            const isResponseReduce = response === R.Response.Reduce;
            const mitigatingControls =
                node.type === "ExpandedRiskNode"
                    ? (node.content?.mitigatingControls ?? [])
                    : [];
            const hasMitigatingControls = mitigatingControls.length > 0;
            const validationFailed =
                requiresControlOnReduction && isResponseReduce
                    ? !hasMitigatingControls
                    : false;
            const customizedValidations: CustomizedValidations = {};
            if (validationFailed) {
                customizedValidations["mitigatingControls"] = {
                    validateStatus: "error",
                    help: t("RiskMaps_Edit_Error_RequiredControlOnReduction"),
                };
            }
            const notFilledFields = getFieldIdsWithoutValueForAssessment(
                riskDirectoryView,
                metadataExtractors,
                node
            );
            for (const field of notFilledFields) {
                customizedValidations[field] = {
                    validateStatus: "error",
                    help: t("RiskMaps_Edit_Error_RequiredForAssessment"),
                };
            }
            if (Object.keys(customizedValidations).length > 0) {
                this.setState((prevState) => ({
                    ...prevState,
                    customizedValidations,
                }));
                throw new Error("Validation error found");
            }
        }

        // XXX DO NOT RESOLVE THIS PROPS BEFORE THE ACTION IS CALLED
        // Otherwise you will work on stale data.
        this.queue.add(() =>
            this.props.updateNode(() => {
                const { node } = this.props;

                switch (node.type) {
                    case "Process":
                        throw new Error(
                            "RiskDirectory & Processes don't have content"
                        );
                    case "ExpandedRiskNode": {
                        const {
                            risk: {
                                content: { scope },
                            },
                            entityId,
                        } = node;
                        switch (scope.type) {
                            case "GlobalWithContent":
                                return {
                                    scope: {
                                        type: "UpdatedGlobalWithContent",
                                        entityIdContent: {
                                            [entityId]: { assessmentStatus },
                                        },
                                    },
                                };
                            case "LocalWithContent":
                                return {
                                    scope: {
                                        type: "UpdatedLocalWithContent",
                                        content: { assessmentStatus },
                                    },
                                };
                            default:
                                return assertNever(scope);
                        }
                    }
                }
            }, this.props.node)
        );
    };

    renderAssessmentCheckbox = (disabled: boolean) => {
        const { node, ajaxManager, orgProfileCookie } = this.props;

        if (node.type === "Process") {
            return null;
        }

        const assessment = node.content?.assessmentStatus;
        const timezone: IanaTimezoneView = {
            name: orgProfileCookie?.timezone || "Europe/Copenhagen",
        };

        if (assessment?.type === "Assessed") {
            const { userId, at } = assessment;

            return (
                <AsyncComponent<UserViewDto>
                    key="key-assessed"
                    promise={() => getUsersUserId(userId)}
                    renderLoading={() => <PageSpinner />}
                    renderError={(code) => {
                        return <LoggedInHTTPError code={code} />;
                    }}
                    renderContent={(user) => {
                        return (
                            <AssessmentCheckbox
                                status={{
                                    assessmentStatus: "assessed",
                                    at,
                                    timezone,
                                    user,
                                }}
                                disabled={disabled}
                                onToggle={() => {
                                    this.changeAsssesmentStatus("unassessed");
                                }}
                            />
                        );
                    }}
                    ajaxManager={ajaxManager}
                />
            );
        } else {
            return (
                <AssessmentCheckbox
                    status={{ assessmentStatus: "unassessed" }}
                    disabled={disabled}
                    onToggle={() => {
                        this.changeAsssesmentStatus("assessed");
                    }}
                />
            );
        }
    };

    renderLastAssessmentStatus = () => {
        const { node, ajaxManager, orgProfileCookie } = this.props;
        if (node.type === "Process") {
            return null;
        }

        const assessment = node.content?.assessmentStatus;

        if (assessment?.type === "PreviouslyAssessed") {
            const { userId, at } = assessment;
            const timezone: IanaTimezoneView = {
                name: orgProfileCookie?.timezone || "Europe/Copenhagen",
            };

            return (
                <>
                    <Divider />
                    <UserWithTimestampStatus
                        key="key-previously-assessed"
                        user={userId}
                        at={at}
                        timezone={timezone}
                        atHeader={t("RiskMaps_Edit_LastAssessment")}
                        userHeader={t("RiskMaps_Edit_AssessedBy")}
                        ajaxManager={ajaxManager}
                    />
                </>
            );
        }

        return null;
    };

    render = () => {
        const { onClose, historyMode, historyDate, node, canEditNode } =
            this.props;
        const { currentTab } = this.state;
        const activityTab: TabSpec = {
            id: ACTIVITY_TAB,
            label: t("RiskDirectory_NodeUpdate_ResponseActivity"),
            onClick: this.handleTabClick,
        };
        const detailsTab: TabSpec = {
            id: DETAILS_TAB,
            label: t("RiskDirectory_NodeUpdate_ResponseDetails"),
            onClick: this.handleTabClick,
        };

        const tabsWithoutActivity: TabSpec[] = [detailsTab];
        const isProcessNode = node.type === "Process";
        const tabs: TabSpec[] = isProcessNode
            ? tabsWithoutActivity
            : [detailsTab, activityTab];

        const drawerFooterBuilder = () =>
            this.renderAssessmentCheckbox(!canEditNode(node));

        return (
            <Drawer
                onCancel={() => {
                    if (this.queue.size === 0 && this.queue.pending === 0) {
                        onClose();
                    } else {
                        emitNotification(
                            "warning",
                            t("RiskDirectory_NodeUpdate_SavingYourData")
                        );
                    }
                }}
                buildFooter={
                    currentTab === DETAILS_TAB ? drawerFooterBuilder : undefined
                }
                buildHeader={() => (
                    <Tabs
                        className={theme.tabs}
                        tabSpecs={tabs}
                        currentTabId={this.state.currentTab}
                    />
                )}
            >
                {historyMode && (
                    <Alert
                        className={theme.historicalDataAlert}
                        message={t(
                            "RiskMap_History_Drawer_HistoricDataAlert_Message"
                        )}
                        description={t(
                            "RiskMap_History_Drawer_HistoricDataAlert_Description",
                            { date: formatMoment(historyDate ?? moment()) }
                        )}
                        type="info"
                    />
                )}
                {this.renderTabContent()}
            </Drawer>
        );
    };

    private renderTabContent = (): React.ReactNode => {
        const detailsTab = this.renderDetailsTabContent();
        const activityTab = this.renderActivityTabContent();

        // NOTE: The 2 tabs content are rendered in the DOM and CONDITIONALLY DISPLAYED.
        // The rationale is that the FileUploader should NOT be unmounted, otherwise the upload
        // of potential files could be canceled. Conditionally displaying instead of conditionally rendering
        // makes sure that switching between tabs in the drawer wont accidentally cancel the upload.
        return (
            <>
                <ConditionalDisplay
                    displayed={this.state.currentTab === DETAILS_TAB}
                >
                    {detailsTab}
                </ConditionalDisplay>
                <ConditionalDisplay
                    displayed={this.state.currentTab === ACTIVITY_TAB}
                >
                    {activityTab}
                </ConditionalDisplay>
            </>
        );
    };

    private resetValidationForField(fieldId: string) {
        this.setState((prevState) => {
            const customizedValidations = new Map(
                Object.entries(prevState.customizedValidations)
            );
            customizedValidations.delete(fieldId);
            return {
                customizedValidations: Object.fromEntries(
                    customizedValidations
                ),
            };
        });
    }

    private renderDetailsTabContent = (): JSX.Element => {
        const {
            riskDirectoryView,
            node,
            entities,
            allControls,
            metadataExtractors,
            common,
            rootNode,
            canEditNode,
            updateRootNodeLinkedResources,
        } = this.props;
        const {
            sections: riskDirectoryLevel,
            matchCount: riskDirectoryFieldCount,
        } = filterAndResolveDirectorySections(
            riskDirectoryView,
            metadataExtractors,
            node,
            {
                riskDirectoryLevel: true,
                entityLevel: false,
            }
        );
        const { sections: entityLevel, matchCount: entityFieldCount } =
            filterAndResolveDirectorySections(
                riskDirectoryView,
                metadataExtractors,
                node,
                {
                    riskDirectoryLevel: false,
                    entityLevel: true,
                }
            );

        let entityLevelProps:
            | { type: "READ_ONLY"; node: R.ProcessNode | T.ExpandedRiskNode }
            | {
                  type: "EDIT";
                  node: R.ProcessNode | T.ExpandedRiskNode;
                  onChangeFieldValueAsync: <PV>(
                      fieldId: Guid,
                      promiseValue: Promise<PV>,
                      callback: (
                          prevState: MetadataInstanceValue | undefined,
                          promisedValue: PV
                      ) => MetadataInstanceValue | null
                  ) => Promise<void>;
              };
        let mitigatingControlsProps:
            | { type: "READ_ONLY" }
            | {
                  type: "EDIT";
                  onUpdate: (
                      callback: (
                          mitigatingControls:
                              | Array<UpdatedMitigatingControl>
                              | undefined
                      ) => Array<UpdatedMitigatingControl> | undefined
                  ) => Promise<void>;
              };

        const assessmentStatus = this.renderLastAssessmentStatus();

        const canEditValues =
            canEditNode(node) && this.canEditTakingIntoAccountTheAssessment();

        if (canEditValues) {
            entityLevelProps = {
                type: "EDIT",
                // XXX DO NOT RESOLVE THIS PROPS BEFORE THE ACTION IS CALLED
                // Otherwise you will work on stale data.
                onChangeFieldValueAsync: (fieldId, promiseValue, callback) => {
                    const promiseAction = async () => {
                        const value = await promiseValue;
                        const action: T.NodeUpdateAction<T.MapNode> = (
                            node: T.MapNode
                        ) => {
                            if (node.type === "RiskDirectory") {
                                throw new Error("Trying to update Root Node");
                            }
                            this.resetValidationForField(fieldId);
                            const newMetadataValue = callback(
                                node.content?.metadataValues[fieldId],
                                value
                            );
                            return toUpdateNodePayload(
                                node,
                                fieldId,
                                newMetadataValue
                            );
                        };
                        await this.props.updateNode(action, this.props.node);
                    };
                    return this.queue.add(promiseAction);
                },
                node,
            };
            mitigatingControlsProps = {
                type: "EDIT",
                // XXX DO NOT RESOLVE THIS PROPS BEFORE THE ACTION IS CALLED
                // Otherwise you will work on stale data.
                onUpdate: (callback) =>
                    this.queue.add(() =>
                        this.props.updateNode(() => {
                            const { node } = this.props;

                            switch (node.type) {
                                case "Process":
                                    throw new Error(
                                        "RiskDirectory & Processes don't have local content"
                                    );
                                case "ExpandedRiskNode": {
                                    const {
                                        risk: {
                                            content: { scope },
                                        },
                                        entityId,
                                        content,
                                    } = node;
                                    const mitigatingControls = callback(
                                        content?.mitigatingControls
                                    );
                                    if (mitigatingControls === undefined) {
                                        return undefined;
                                    }
                                    this.resetValidationForField(
                                        "mitigatingControls"
                                    );
                                    switch (scope.type) {
                                        case "GlobalWithContent":
                                            return {
                                                scope: {
                                                    type: "UpdatedGlobalWithContent",
                                                    entityIdContent: {
                                                        [entityId]: {
                                                            mitigatingControls,
                                                        },
                                                    },
                                                },
                                            };
                                        case "LocalWithContent":
                                            return {
                                                scope: {
                                                    type: "UpdatedLocalWithContent",
                                                    content: {
                                                        mitigatingControls,
                                                    },
                                                },
                                            };
                                        default:
                                            return assertNever(scope);
                                    }
                                }
                            }
                        }, this.props.node)
                    ),
            };
        } else {
            entityLevelProps = {
                type: "READ_ONLY",
                node,
            };
            mitigatingControlsProps = {
                type: "READ_ONLY",
            };
        }

        return (
            <MapDrawerForm
                node={node}
                entities={entities}
                riskDirectoryFieldCount={riskDirectoryFieldCount}
                riskDirectoryLevel={riskDirectoryLevel}
                metadataExtractors={metadataExtractors}
                entityFieldCount={entityFieldCount}
                entityLevel={entityLevel}
                entityLevelProps={entityLevelProps}
                mitigatingControlsProps={mitigatingControlsProps}
                common={common}
                allControls={allControls}
                rootNode={rootNode}
                assessmentStatus={assessmentStatus}
                customizedValidations={this.state.customizedValidations}
                specialProperties={this.specialProperties}
                getResponse={this.getResponse}
                updateRootNodeLinkedResources={updateRootNodeLinkedResources}
            />
        );
    };

    private renderActivityTabContent = (): JSX.Element => {
        const {
            node,
            ajaxManager,
            riskDirectoryView,
            onDateChange,
            historyDate,
        } = this.props;
        if (node.type !== "ExpandedRiskNode") {
            return <></>;
        }
        const riskNodeId = node.risk.id;
        const entityId = node.entityId;
        const riskDirectoryId = riskDirectoryView.content.resource.id;

        return (
            <AsyncComponent
                key={`risk-activity-log-${riskNodeId}`}
                ref={this.asyncComponent}
                ajaxManager={ajaxManager}
                promise={() =>
                    getRisksRiskDirectoryIdNodesNodeIdActivityLog(
                        riskDirectoryId,
                        riskNodeId,
                        { entity_id: entityId, at: historyDate?.toISOString() }
                    )
                }
                renderLoading={() => <PageSpinner />}
                renderError={(code) => <LoggedInHTTPError code={code} />}
                renderContent={(dto) => (
                    <RiskActivityLog
                        onDateChange={(date: Moment) =>
                            onDateChange({ type: "FROM_EXACT_TIMESTAMP", date })
                        }
                        historyDate={historyDate}
                        activityLog={dto.content.resource}
                        linkedResources={dto.linkedResources}
                    />
                )}
            />
        );
    };

    private handleTabClick = (param: SelectedTabParam) => {
        switch (param.key) {
            case ACTIVITY_TAB:
                this.setState({ currentTab: ACTIVITY_TAB });
                break;
            case DETAILS_TAB:
                this.setState({ currentTab: DETAILS_TAB });
                break;
            default:
                throw new Error("Could not set currentTab - invalid key");
        }
    };

    private getResponse = (): R.ResponseEnum | undefined => {
        const { riskDirectoryView, node, responseEnumMap, metadataExtractors } =
            this.props;
        if (node.type !== "ExpandedRiskNode") {
            return undefined;
        }
        const responseExtractor = metadataExtractors.find(
            (ext) =>
                ext.id ===
                riskDirectoryView.content.resource.structure.blessedFieldIds
                    .responseId
        );
        if (responseExtractor === undefined) {
            throw new Error("Can't find response extractor");
        }
        const instance = responseExtractor.getValue(node);
        if (instance === undefined) {
            throw new Error("The response should always have an instance");
        }
        switch (instance.type) {
            case "DropdownInstance": {
                return getEnum(instance, responseEnumMap);
            }
            case "MetadataError": {
                return undefined;
            }
            default: {
                throw new Error(
                    "The response should always be a dropdown or an error, but got " +
                        instance.type
                );
            }
        }
    };

    private specialProperties = (
        extractor: DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>,
        instance: T.MetadataInstance,
        _type: "READ_ONLY" | "EDIT" | "CREATE"
    ): PartialSharedProperties | undefined => {
        const {
            riskDirectoryView: {
                content: {
                    resource: {
                        structure: {
                            blessedFieldIds: {
                                responseId,
                                residualLikelihoodId,
                                residualImpactId,
                            },
                        },
                    },
                },
            },
            node,
            metadataExtractors,
            responseEnumMap,
        } = this.props;

        switch (instance.type) {
            case "DropdownInstance":
                // Dropdown with a formula are never editable
                if (hasFormula(instance.fieldType)) {
                    return { type: "READ_ONLY" };
                }
                if (
                    extractor.id === residualImpactId ||
                    extractor.id === residualLikelihoodId
                ) {
                    const responseExtractor = metadataExtractors.find(
                        (ext) => ext.id === responseId
                    );
                    if (!responseExtractor) {
                        throw new Error("Could not find response extractor");
                    }
                    const responseInstance = responseExtractor.getValue(node);
                    if (responseInstance === undefined) {
                        throw new Error("No response instance");
                    }
                    if (responseInstance.type !== "DropdownInstance") {
                        throw new Error("The response should be a dropdown");
                    }
                    // Impact and likelihood are only editable if the response is reduce
                    const responseEnum = getEnum(
                        responseInstance,
                        responseEnumMap
                    );
                    return responseEnum !== R.Response.Reduce
                        ? { type: "READ_ONLY" }
                        : undefined;
                }

                return undefined;

            case "RadioButtonGroupInstance":
            case "NumericInputInstance":
                // Anything else with a formula is also not editable
                if (hasFormula(instance.fieldType)) {
                    return { type: "READ_ONLY" };
                } else {
                    return undefined;
                }
            default:
                return undefined;
        }
    };

    private canEditTakingIntoAccountTheAssessment = (): boolean => {
        const { node } = this.props;
        switch (node.type) {
            case "Process":
                return false;
            case "ExpandedRiskNode": {
                const assessmentStatus = node.content?.assessmentStatus;

                if (assessmentStatus?.type === "Assessed") {
                    return false;
                }
            }
        }
        return true;
    };
}

function toUpdateNodePayload(
    node: R.ProcessNode | T.ExpandedRiskNode,
    fieldId: string,
    newValue: MetadataInstanceValue | null
): R.UpdatedRiskDirectoryNode {
    switch (node.type) {
        case "Process":
            throw new Error(
                "RiskDirectory & Processes don't have local content"
            );
        case "ExpandedRiskNode": {
            const {
                risk: {
                    content: { scope },
                },
                entityId,
            } = node;
            switch (scope.type) {
                case "GlobalWithContent":
                    return {
                        scope: {
                            type: "UpdatedGlobalWithContent",
                            entityIdContent: {
                                [entityId]: {
                                    metadataValues: { [fieldId]: newValue },
                                },
                            },
                        },
                    };
                case "LocalWithContent":
                    return {
                        scope: {
                            type: "UpdatedLocalWithContent",
                            content: {
                                metadataValues: { [fieldId]: newValue },
                            },
                        },
                    };
                default:
                    return assertNever(scope);
            }
        }
        default:
            return assertNever(node);
    }
}

interface MapNodeDrawerProps {
    riskDirectoryView: R.RiskDirectoryView;
    rootNode: RiskDirectoryNodeDto;
    node: R.ProcessNode | T.ExpandedRiskNode;
    entities: Record<number, EntityView>;
    allControls: ControlOverviewListDto;
    metadataExtractors: Array<
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>
    >;
    responseEnumMap: EnumMap<R.ResponseEnum>;
    onClose: () => void;
    updateNode: (
        action: T.NodeUpdateAction<T.MapNode>,
        targetNode: T.MapNode
    ) => Promise<void>;
    common: T.CommonGetters;
    historyMode: boolean;
    historyDate: Moment | null;
    onDateChange: (date: DateChange) => void;
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile | null;
    canEditNode: (node: R.ProcessNode | T.ExpandedRiskNode) => boolean;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

interface MapNodeDrawerState {
    customizedValidations: CustomizedValidations;
    currentTab: CurrentTab;
}

interface RiskActivityLogProps {
    activityLog: ActivityLogForRiskNodeActivity;
    linkedResources: RiskActivityLogLinkedResources;
    onDateChange: (date: Moment) => void;
    historyDate: Moment | null;
}

class RiskActivityLog extends React.PureComponent<RiskActivityLogProps> {
    render = () => {
        const { activityLog, linkedResources, onDateChange, historyDate } =
            this.props;

        return (
            <ActivityLog
                renderItem={renderRiskActivityLogItem}
                activityLog={activityLog}
                linkedResources={linkedResources}
                onDateChange={onDateChange}
                historyDate={historyDate}
            />
        );
    };
}

function getMetadataValueName(
    linkedResources: RiskActivityLogLinkedResources,
    metadataValue: MetadataValue,
    updatedAt: Moment
): string {
    switch (metadataValue.type) {
        case "Literal":
            return metadataValue.value;
        case "Option":
            const value = getValueOfResourceAt(
                linkedResources.metadataOptions,
                metadataValue.value,
                updatedAt
            );
            if (!value) {
                throw new Error(
                    "No matching value for metadata value option for value " +
                        metadataValue.value +
                        " was found in " +
                        JSON.stringify(linkedResources.metadataOptions) +
                        " for date " +
                        updatedAt.toISOString()
                );
            }
            return value;
        case "OptionGroup":
            const options = metadataValue.values.map((v) =>
                getValueOfResourceAt(
                    linkedResources.metadataOptions,
                    v,
                    updatedAt
                )
            );
            const optionsLabel = options.join(", ");
            return optionsLabel;
        case "AttachedFiles":
            const findBlob = (blobId: string) =>
                linkedResources.blobs.find((blob) => blob.id === blobId);
            return metadataValue.values
                .map((blobId) => findBlob(blobId)?.fileName)
                .filter((fileName) => fileName !== undefined)
                .join(", ");
        default:
            return assertNever(metadataValue);
    }
}

type MaybeFoundBlob =
    | {
          kind: "Found";
          blob: SavedBlob;
      }
    | {
          kind: "NotFound";
          blobId: string;
      };

function renderRiskActivityLogItem(
    linkedResources: RiskActivityLogLinkedResources,
    item: RiskNodeActivity,
    userId: number,
    at: Moment
): React.ReactNode {
    const userFromLinkedResources = linkedResources.users.find(
        (r) => r.resource.id === userId
    )!.resource;
    const maybeFindBlobFromLinkedResources = (
        blobId: string
    ): MaybeFoundBlob => {
        const maybeBlob = linkedResources.blobs.find(
            (blob) => blob.id === blobId
        );
        if (maybeBlob === undefined) {
            return { kind: "NotFound", blobId };
        }
        return { kind: "Found", blob: maybeBlob };
    };
    const user = escapeMarkdownValue(formatUserName(userFromLinkedResources));
    const entityName = escapeMarkdownValue(
        getValueAt(linkedResources.entityName, at) ?? ""
    );
    const riskName = escapeMarkdownValue(
        getValueAt(linkedResources.riskName, at) ?? ""
    );
    const getHistoricControl = (id: string): HistoricControl => {
        const controlOverTime = linkedResources.controls.find(
            (r) => r.id === id
        )!;
        return getValueAt(controlOverTime.values, at)!;
    };
    const getControlTitle = (id: string) => {
        return getHistoricControl(id).title;
    };

    switch (item.type) {
        case "MetadataUpdate":
            const value = escapeMarkdownValue(
                getMetadataValueName(linkedResources, item.value, at)
            );
            const updatedName = escapeMarkdownValue(
                getValueOfResourceAt(
                    linkedResources.metadataColumns,
                    item.columnId,
                    at
                ) ?? ""
            );
            return renderMarkdown(
                t("RiskMap_History_Drawer_Update_Text", {
                    user,
                    name: updatedName,
                    value,
                })
            );
        case "MetadataDeletion":
            const deletedName = escapeMarkdownValue(
                getValueOfResourceAt(
                    linkedResources.metadataColumns,
                    item.columnId,
                    at
                ) ?? ""
            );
            return renderMarkdown(
                t("RiskMap_History_Drawer_Delete_Text", {
                    user,
                    name: deletedName,
                })
            );
        case "Assignment":
            return renderMarkdown(
                t("RiskMap_History_Drawer_Assign_Text", {
                    user,
                    name: riskName,
                    value: entityName,
                })
            );
        case "Unassignment":
            return renderMarkdown(
                t("RiskMap_History_Drawer_Unassign_Text", {
                    user,
                    name: riskName,
                    value: entityName,
                })
            );
        case "Assessment":
            return renderMarkdown(
                t("RiskMap_History_Drawer_Assessed_Text", { user })
            );
        case "Unassessment":
            return renderMarkdown(
                t("RiskMap_History_Drawer_Unassessed_Text", { user })
            );
        case "AddMitigatingAction":
            const addedControl = escapeMarkdownValue(
                getControlTitle(item.controlId)
            );
            const initialCoverage = item.riskCoverage;
            return renderMarkdown(
                t("RiskMap_History_Drawer_AddMitigatingAction_Text", {
                    user,
                    control: addedControl,
                    coverage: initialCoverage,
                })
            );
        case "RemoveMitigatingAction":
            const removedControl = escapeMarkdownValue(
                getControlTitle(item.controlId)
            );
            return renderMarkdown(
                t("RiskMap_History_Drawer_RemoveMitigatingAction_Text", {
                    user,
                    control: removedControl,
                })
            );
        case "ChangeCoverage":
            const coverage = item.riskCoverage;
            const modifiedControl = escapeMarkdownValue(
                getControlTitle(item.controlId)
            );
            return renderMarkdown(
                t("RiskMap_History_Drawer_ChangeCoverage_Text", {
                    user,
                    control: modifiedControl,
                    coverage,
                })
            );
        case "AttachFiles":
            const colNameAttachFile = escapeMarkdownValue(
                getValueOfResourceAt(
                    linkedResources.metadataColumns,
                    item.columnId,
                    at
                ) ?? ""
            );
            const blobsAttached = item.blobIds.map((blobId) =>
                maybeFoundBlobToMarkdownLink(
                    maybeFindBlobFromLinkedResources(blobId)
                )
            );
            const filesAttached = blobsAttached.join(", ");
            return renderMarkdown(
                t("RiskMap_History_Drawer_AttachFile_Text", {
                    user,
                    files: filesAttached,
                    count: blobsAttached.length,
                    name: colNameAttachFile,
                })
            );
        case "RemoveFiles":
            const colNameRemoveFile = escapeMarkdownValue(
                getValueOfResourceAt(
                    linkedResources.metadataColumns,
                    item.columnId,
                    at
                ) ?? ""
            );
            const blobsRemoved = item.blobIds.map((blobId) =>
                maybeFoundBlobToMarkdownLink(
                    maybeFindBlobFromLinkedResources(blobId)
                )
            );
            const filesRemoved = blobsRemoved.join(", ");
            return renderMarkdown(
                t("RiskMap_History_Drawer_RemoveFile_Text", {
                    user,
                    files: filesRemoved,
                    count: blobsRemoved.length,
                    name: colNameRemoveFile,
                })
            );
        default:
            return assertNever(item);
    }
}

function maybeFoundBlobToMarkdownLink(maybeBlob: MaybeFoundBlob): string {
    switch (maybeBlob.kind) {
        case "Found":
            const blobUrl = formatBlobUrl(maybeBlob.blob.id);
            const blobFileName = escapeMarkdownValue(maybeBlob.blob.fileName);
            return `[${blobFileName}](${blobUrl})`;
        case "NotFound":
            // NOTE: The blobs should ALWAYS be in the
            // linked resources of the activity log.
            // An undefined blob should only happen on a local setup, where blobs
            // are currently not persisted after the docker environment is stopped.
            Sentry.captureException("Unknown blob " + maybeBlob.blobId);
            return `**Unknown file ${maybeBlob.blobId}**`;
        default:
            return assertNever(maybeBlob);
    }
}

export { MapNodeDrawer };
